import { Check, Plus, X } from 'lucide-react';
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Select } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import type { PathType } from '../types';

const TYPE_OPTIONS: { value: PathType; label: string }[] = [
  { value: 'string', label: 'String' },
  { value: 'number', label: 'Number' },
  { value: 'boolean', label: 'Boolean' },
  { value: 'object', label: 'Object' },
];

interface AddKeyControlProps {
  parentPath: string;
  onAddPath: (path: string, type: PathType) => void;
  disabled?: boolean;
}

export const AddKeyControl: React.FC<AddKeyControlProps> = ({
  parentPath,
  onAddPath,
  disabled = false,
}) => {
  const [open, setOpen] = useState(false);
  const [key, setKey] = useState('');
  const [type, setType] = useState<PathType>('string');

  const trimmedKey = key.trim();
  const canSubmit = trimmedKey.length > 0;

  const close = () => {
    setOpen(false);
    setKey('');
    setType('string');
  };

  const handleSubmit = () => {
    if (disabled || !canSubmit) return;
    const fullPath =
      parentPath === '' ? trimmedKey : `${parentPath}.${trimmedKey}`;
    onAddPath(fullPath, type);
    close();
  };

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        className={cn(
          'flex items-center gap-1 rounded px-1 py-0.5 text-xs text-text-secondary hover:bg-accent hover:text-text-primary',
          disabled &&
            'cursor-not-allowed opacity-50 hover:bg-transparent hover:text-text-secondary',
        )}
        aria-label="Feld hinzufügen"
        title={
          disabled ? 'Übergeordneter Pfad ist zum Löschen markiert' : undefined
        }
      >
        <Plus className="size-3.5" />
        <span>Feld hinzufügen</span>
      </button>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-1 rounded border border-dashed border-gray-300 px-1 py-0.5">
      <input
        type="text"
        value={key}
        onChange={(event) => setKey(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter') {
            event.preventDefault();
            handleSubmit();
          } else if (event.key === 'Escape') {
            event.preventDefault();
            close();
          }
        }}
        placeholder="neuer Schlüssel"
        autoFocus
        className={cn(
          'h-8 min-w-[8rem] flex-1 rounded-md border border-gray-300 bg-white px-2 py-1 font-mono text-sm',
          'focus:border-blue-500 focus:ring-1 focus:ring-blue-500',
        )}
      />
      <Select
        value={type}
        onChange={(event) => setType(event.target.value as PathType)}
        options={TYPE_OPTIONS}
        className="h-8 w-32 py-1"
      />
      <Button
        type="button"
        size="sm"
        variant="secondary"
        onClick={handleSubmit}
        disabled={!canSubmit}
        aria-label="Bestätigen"
      >
        <Check className="size-3.5" />
      </Button>
      <Button
        type="button"
        size="sm"
        variant="ghost"
        onClick={close}
        aria-label="Abbrechen"
      >
        <X className="size-3.5" />
      </Button>
    </div>
  );
};
